import React, { useState, useEffect } from 'react';
import { Calculator } from 'lucide-react';
import { SEO } from '../components/SEO';

interface WoodState {
  length: string;
  width: string;
  height: string;
  quantity: string;
  woodType: 'pine' | 'oak' | 'beech' | 'maple' | 'spruce' | 'walnut';
  moistureContent: 'dry' | 'semi-dry' | 'fresh';
}

// Density in kg/m³ for different wood types and moisture contents
const woodDensities: Record<WoodState['woodType'], Record<WoodState['moistureContent'], number>> = {
  pine: {
    dry: 450,
    'semi-dry': 550,
    fresh: 700
  },
  oak: {
    dry: 700,
    'semi-dry': 850,
    fresh: 1000
  },
  beech: {
    dry: 680,
    'semi-dry': 830,
    fresh: 980
  },
  maple: {
    dry: 620,
    'semi-dry': 770,
    fresh: 920
  },
  spruce: {
    dry: 400,
    'semi-dry': 500,
    fresh: 650
  },
  walnut: {
    dry: 660,
    'semi-dry': 810,
    fresh: 960
  }
};

const woodNames: Record<WoodState['woodType'], string> = {
  pine: 'Bor',
  oak: 'Hrast',
  beech: 'Bukva',
  maple: 'Javor',
  spruce: 'Smreka',
  walnut: 'Orah'
};

const moistureNames: Record<WoodState['moistureContent'], string> = {
  dry: 'Suho (< 15%)',
  'semi-dry': 'Polusuho (15-30%)',
  fresh: 'Svježe (> 30%)'
};

function KalkulatorDrveta() {
  const [values, setValues] = useState<WoodState>({
    length: '',
    width: '',
    height: '',
    quantity: '1',
    woodType: 'oak',
    moistureContent: 'dry'
  });

  const [results, setResults] = useState<{
    volume: number;
    weight: number;
    priceEstimate: { min: number; max: number };
  } | null>(null);

  const calculateResults = () => {
    const length = parseFloat(values.length);
    const width = parseFloat(values.width);
    const height = parseFloat(values.height);
    const quantity = parseInt(values.quantity) || 1;
    
    if (isNaN(length) || isNaN(width) || isNaN(height)) {
      return null;
    }

    // Calculate volume in cubic meters
    const volumePerPiece = (length * width * height) / 1000000; // Convert from cm³ to m³
    const totalVolume = volumePerPiece * quantity;

    // Calculate weight based on wood density
    const density = woodDensities[values.woodType][values.moistureContent];
    const totalWeight = totalVolume * density;

    // Estimate price (rough estimation in EUR per m³)
    const priceRanges: Record<WoodState['woodType'], { min: number; max: number }> = {
      pine: { min: 200, max: 300 },
      oak: { min: 600, max: 800 },
      beech: { min: 400, max: 600 },
      maple: { min: 500, max: 700 },
      spruce: { min: 180, max: 280 },
      walnut: { min: 800, max: 1000 }
    };

    const priceRange = priceRanges[values.woodType];
    const priceEstimate = {
      min: totalVolume * priceRange.min,
      max: totalVolume * priceRange.max
    };

    return {
      volume: parseFloat(totalVolume.toFixed(4)),
      weight: parseFloat(totalWeight.toFixed(2)),
      priceEstimate: {
        min: parseFloat(priceEstimate.min.toFixed(2)),
        max: parseFloat(priceEstimate.max.toFixed(2))
      }
    };
  };

  const handleInputChange = (field: keyof WoodState, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    if (values.length && values.width && values.height) {
      const results = calculateResults();
      setResults(results);
    }
  }, [values]);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Drveta",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte volumen, težinu i okvirnu cijenu drvene građe. Podržava različite vrste drva i stupnjeve vlažnosti.",
    "url": "https://online-kalkulator.com/kalkulator-drveta",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    }
  };

  return (
    <>
      <SEO 
        title="Kalkulator Drveta | Izračun Volumena i Težine Drveta"
        description="Izračunajte volumen, težinu i okvirnu cijenu drvene građe. Precizni izračuni za različite vrste drva i stupnjeve vlažnosti."
        canonicalUrl="/kalkulator-drveta"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Calculator className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Drveta</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Dimensions */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dužina (cm)
                  </label>
                  <input
                    type="number"
                    value={values.length}
                    onChange={(e) => handleInputChange('length', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Širina (cm)
                  </label>
                  <input
                    type="number"
                    value={values.width}
                    onChange={(e) => handleInputChange('width', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 20"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Debljina (cm)
                  </label>
                  <input
                    type="number"
                    value={values.height}
                    onChange={(e) => handleInputChange('height', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 5"
                  />
                </div>
              </div>

              {/* Wood Type and Moisture */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vrsta drveta
                  </label>
                  <select
                    value={values.woodType}
                    onChange={(e) => handleInputChange('woodType', e.target.value as WoodState['woodType'])}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {Object.entries(woodNames).map(([value, name]) => (
                      <option key={value} value={value}>{name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sadržaj vlage
                  </label>
                  <select
                    value={values.moistureContent}
                    onChange={(e) => handleInputChange('moistureContent', e.target.value as WoodState['moistureContent'])}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {Object.entries(moistureNames).map(([value, name]) => (
                      <option key={value} value={value}>{name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Količina (komada)
                </label>
                <input
                  type="number"
                  value={values.quantity}
                  onChange={(e) => handleInputChange('quantity', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="npr. 1"
                  min="1"
                />
              </div>

              {/* Results */}
              {results && (
                <div className="space-y-4 border-t pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-gray-600 text-sm mb-1">Volumen</div>
                      <div className="text-xl font-bold text-blue-600">{results.volume} m³</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-gray-600 text-sm mb-1">Težina</div>
                      <div className="text-xl font-bold text-blue-600">{results.weight} kg</div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg text-center">
                    <div className="text-gray-600 text-sm mb-1">Okvirna cijena</div>
                    <div className="text-xl font-bold text-blue-600">
                      {results.priceEstimate.min} - {results.priceEstimate.max} €
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Drveta</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator drveta je koristan alat koji vam pomaže izračunati volumen, težinu i
                  okvirnu cijenu drvene građe. Uzima u obzir različite vrste drva, njihovu gustoću
                  i sadržaj vlage za preciznije izračune.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Vrste Drva i Gustoća</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Tvrdo drvo</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Hrast: 700-1000 kg/m³</li>
                      <li>Bukva: 680-980 kg/m³</li>
                      <li>Javor: 620-920 kg/m³</li>
                      <li>Orah: 660-960 kg/m³</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Meko drvo</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Bor: 450-700 kg/m³</li>
                      <li>Smreka: 400-650 kg/m³</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Sadržaj Vlage</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Suho drvo (&lt;15%): Idealno za unutarnju upotrebu</li>
                  <li>Polusuho drvo (15-30%): Pogodno za vanjsku upotrebu</li>
                  <li>Svježe drvo (&gt;30%): Netom posječeno drvo</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Unesite dimenzije drveta u centimetrima</li>
                  <li>Odaberite vrstu drveta</li>
                  <li>Odaberite sadržaj vlage</li>
                  <li>Unesite količinu komada</li>
                  <li>Rezultati će se automatski izračunati</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Dimenzije se unose u centimetrima</li>
                  <li>Volumen se prikazuje u kubnim metrima (m³)</li>
                  <li>Težina se prikazuje u kilogramima (kg)</li>
                  <li>Cijene su okvirne i mogu varirati ovisno o:
                    <ul className="list-none pl-6 pt-2 space-y-1">
                      <li>- Kvaliteti drveta</li>
                      <li>- Tržišnim uvjetima</li>
                      <li>- Lokaciji</li>
                      <li>- Količini</li>
                    </ul>
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Savjeti za Odabir Drveta</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Za unutarnje radove koristite suho drvo</li>
                  <li>Za vanjske konstrukcije birajte otporne vrste</li>
                  <li>Provjerite kvalitetu i postojanje čvorova</li>
                  <li>Uzmite u obzir težinu za transport i montažu</li>
                  <li>Razmislite o namjeni prije odabira vrste drveta</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorDrveta;